/**
 * PendingDecisionDialog — okno „Akceptacja" otwierane z WIERSZA listy „Moje czekające".
 *
 * `AcceptanceDialog` potrzebuje danych, których wiersz listy nie niesie: zamrożonej migawki sprawy
 * (z niej wynika, czy cel drogi wymaga wskazania adresata) oraz — dla kroku, na którym decyduje
 * pytający — dróg i flagi „głos za drogą domyślną zamknie etap" (liczy serwer). Ten komponent
 * dociąga detal sprawy (`fetchInstanceDetail`, ten sam klucz co baner na karcie rekordu) i dopiero
 * z niego buduje okno, dokładnie tak jak `ApprovalActionsPanel`: krok = ten z wiersza, drogi, flaga
 * i opcja „Cofnij do poprzedniego etapu" (`stepBack`) = z kroku w detalu.
 *
 * Detal jest ZAWSZE pobierany świeżo przy otwarciu (`refetchOnMount: 'always'` + czekanie na
 * pierwszą UDANĄ odpowiedź po zamontowaniu) — stan z pamięci podręcznej (np. po wizycie na karcie
 * rekordu) mógłby być nieaktualny, a od niego zależy, czy w ogóle można zdecydować. Samo
 * `isFetchedAfterMount` nie wystarcza: TanStack Query ustawia je także po NIEUDANYM pobraniu,
 * a wtedy `data` to wciąż stary stan z pamięci podręcznej — dlatego świeżą odpowiedź liczymy
 * tylko wtedy, gdy pobranie po otwarciu się udało, i zapamiętujemy ją: późniejsze nieudane
 * odświeżenie w tle nie zamienia otwartego okna decyzji z powrotem w błąd (zgubiłoby komentarz
 * wpisany przez osobę decydującą). Zapamiętany detal należy do konkretnej sprawy (`row.instanceId`):
 * po podmianie wiersza na inną sprawę poprzedni detal jest ignorowany.
 *
 * Stany okna:
 *  - ładowanie (z „Anuluj"),
 *  - błąd wczytania → komunikat + „Spróbuj ponownie" (okno decyzji bez migawki mogłoby pominąć
 *    wymagane wskazanie adresata, więc nie otwieramy go „na ślepo"),
 *  - rekord sprawy usunięty (serwer: 404 `TARGET_RECORD_DELETED` — sprawa „nie istnieje" do
 *    przywrócenia kartoteki) → „Nie znaleziono kartoteki." bez „Spróbuj ponownie"; lista jest
 *    odświeżana, żeby wiersz zniknął,
 *  - moduł kartoteki sprawy nieaktywny (serwer: 402 `TARGET_MODULE_INACTIVE` — moduł wyłączono po
 *    otwarciu okna) → „Moduł tej kartoteki jest nieaktywny." bez „Spróbuj ponownie"; lista jest
 *    odświeżana tak samo (serwer ukrywa taką sprawę),
 *  - sprawa niedostępna dla pytającego (serwer: 404 `INSTANCE_NOT_FOUND` — osoba bez podglądu
 *    spraw, która nie może już na niej decydować; serwer nie ujawnia wtedy istnienia sprawy) →
 *    ten sam komunikat co przy kroku, na którym nie można już decydować („nie czeka już na Twoją
 *    decyzję"), bez „Spróbuj ponownie"; lista jest odświeżana, żeby wiersz zniknął,
 *    Wszystkie trzy stany końcowe (rekord usunięty, moduł nieaktywny, sprawa niedostępna) są
 *    zapamiętane per sprawa, a odświeżenie list pomija klucz szczegółów TEJ sprawy: zapytanie bez
 *    danych po ponownym pobraniu traci błąd, więc unieważnienie całego `['processes']` gasiło
 *    komunikat i wywoływało odświeżanie w pętli. Stanów końcowych zapytanie też nie ponawia
 *    (`retryInstanceDetailQuery`, wspólna z kartą rekordu) — komunikat pojawia się od razu, bez
 *    opóźnienia po domyślnym ponowieniu,
 *  - krok, na którym pytający nie może już zdecydować (`canDecide: false` albo kroku nie ma w
 *    detalu — rozstrzygnięty przez kogoś, już zagłosowano) → komunikat; lista jest wtedy odświeżana,
 *    żeby wiersz zniknął,
 *  - właściwe okno decyzji.
 *
 * Po zapisaniu decyzji `AcceptanceDialog` unieważnia dane procesów (lista odświeża się sama),
 * a tu wołamy `onDecided`, żeby wołający zamknął okno. Wołający montuje komponent dopiero, gdy okno
 * ma być otwarte (świeży stan per otwarcie).
 */

import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { hashKey, useQuery, useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { instanceDetailKey, retryInstanceDetailQuery } from '@/hooks/useActiveInstanceForRecord';
import {
  isTargetModuleInactiveError,
  isTargetRecordDeletedError,
  type MyPendingDecisionRow,
  TARGET_MODULE_INACTIVE_I18N_KEY,
  TARGET_RECORD_DELETED_I18N_KEY,
} from '@/lib/processes/approvals-api';
import {
  fetchInstanceDetail,
  type InstanceDetail,
  isInstanceNotFoundError,
} from '@/lib/processes/instance-api';

import { AcceptanceDialog } from './AcceptanceDialog';

export interface PendingDecisionDialogProps {
  /** Wiersz listy, z którego otwarto okno: krok czekający na decyzję i jego sprawa. */
  row: Pick<MyPendingDecisionRow, 'stepInstanceId' | 'instanceId'>;
  /** Droga z klikniętego przycisku; `null` = „Akceptacja" (preselekcja drogi domyślnej). */
  initialRouteKey: string | null;
  /**
   * Okno otwarte z pozycji „Cofnij do poprzedniego etapu" w menu „…" wiersza — przekazywane do
   * `AcceptanceDialog` (cofnięcie wybrane, gdy dostępne w świeżym detalu; inaczej komunikat
   * „nie jest już możliwe"). Domyślnie `false`.
   */
  initialStepBack?: boolean;
  /** Zamknięcie bez decyzji („Anuluj", Esc, tło) albo po rozstrzygnięciu etapu przez kogoś innego. */
  onClose: () => void;
  /** Decyzja zapisana (po toaście i unieważnieniu danych procesów). */
  onDecided: () => void;
  testIdPrefix?: string;
}

/**
 * Stan końcowy sprawy bez szczegółów (odmowa serwera, której ponowienie nie zmieni; zapytanie ich
 * nie ponawia — `retryInstanceDetailQuery`).
 */
type TerminalFailureKind = 'recordDeleted' | 'moduleInactive' | 'instanceGone';

export function PendingDecisionDialog({
  row,
  initialRouteKey,
  initialStepBack = false,
  onClose,
  onDecided,
  testIdPrefix = 'pending-decision',
}: PendingDecisionDialogProps): ReactElement {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const instanceId = row.instanceId;
  const detailQuery = useQuery({
    queryKey: instanceDetailKey(row.instanceId),
    queryFn: () => fetchInstanceDetail(row.instanceId),
    refetchOnMount: 'always',
    retry: retryInstanceDetailQuery,
  });

  // Dane z pamięci podręcznej nie liczą się, dopóki nie przyjdzie UDANA odpowiedź pobrana po
  // otwarciu okna (`isFetchedAfterMount` jest prawdziwe także po błędzie — wtedy `data` to stary
  // stan z cache'u, więc dodatkowo wymagamy braku błędu).
  const freshData =
    detailQuery.isFetchedAfterMount && !detailQuery.isError ? detailQuery.data : undefined;
  // Zapamiętana ostatnia świeża odpowiedź: kolejne nieudane odświeżenie w tle (np. po powrocie
  // do karty przeglądarki) jej nie kasuje. Nowa udana odpowiedź ją podmienia. Pamięć jest
  // przypięta do sprawy (`instanceId` wiersza): gdy wołający podmieni `row` na inną sprawę, detal
  // poprzedniej nie może się pokazać (okno decyzji ani komunikat „nie czeka już" o cudzej sprawie)
  // — do czasu świeżej odpowiedzi dla nowej sprawy okno pokazuje ładowanie.
  const [remembered, setRemembered] = useState<
    { instanceId: string; detail: InstanceDetail } | undefined
  >(undefined);
  if (freshData !== undefined && freshData !== remembered?.detail) {
    setRemembered({ instanceId: row.instanceId, detail: freshData });
  }
  const detail = remembered?.instanceId === row.instanceId ? remembered.detail : undefined;
  const step = detail?.steps.find((s) => s.stepInstanceId === row.stepInstanceId);
  const notActionable = detail !== undefined && step?.canDecide !== true;

  const queryFailed = detail === undefined && detailQuery.isError;
  // Stan końcowy sprawy bez szczegółów: rekord usunięty (serwer: 404 `TARGET_RECORD_DELETED`),
  // moduł jego kartoteki nieaktywny (402 `TARGET_MODULE_INACTIVE`) albo sprawa niedostępna dla
  // pytającego (404 `INSTANCE_NOT_FOUND` — osoba bez podglądu spraw, która nie może już na niej
  // decydować; serwer nie ujawnia wtedy istnienia sprawy). Ponowna próba nic nie zmieni (komunikat
  // bez „Spróbuj ponownie"), a serwer ukrywa taką sprawę na listach. Stan zapamiętujemy per
  // sprawa, bo samo zapytanie go nie utrzyma: zapytanie BEZ danych po ponownym pobraniu traci
  // błąd (status „pending"), więc komunikat gasłby przy każdym odświeżeniu szczegółów.
  const [terminal, setTerminal] = useState<
    { instanceId: string; kind: TerminalFailureKind } | undefined
  >(undefined);
  const failureKind: TerminalFailureKind | undefined = !queryFailed
    ? undefined
    : isTargetRecordDeletedError(detailQuery.error)
      ? 'recordDeleted'
      : isTargetModuleInactiveError(detailQuery.error)
        ? 'moduleInactive'
        : isInstanceNotFoundError(detailQuery.error)
          ? 'instanceGone'
          : undefined;
  // Aktualny błąd rozstrzyga: stan końcowy → zapamiętujemy, inny błąd (np. 500) → zapominamy stary stan
  // końcowy tej sprawy. Brak błędu (pobranie w toku) zostawia zapamiętany stan bez zmian.
  if (queryFailed) {
    const rememberedKind = terminal?.instanceId === row.instanceId ? terminal.kind : undefined;
    if (failureKind !== rememberedKind) {
      setTerminal(
        failureKind === undefined ? undefined : { instanceId: row.instanceId, kind: failureKind },
      );
    }
  }
  // Zapamiętany stan obowiązuje tylko dla tej sprawy i dopóki nie przyszła świeża odpowiedź ze
  // szczegółami (np. po przywróceniu kartoteki) — wtedy rządzi detal.
  const terminalKind: TerminalFailureKind | undefined =
    detail === undefined && terminal?.instanceId === row.instanceId ? terminal.kind : undefined;
  const recordDeleted = terminalKind === 'recordDeleted';
  const moduleInactive = terminalKind === 'moduleInactive';
  // Sprawa niedostępna dla pytającego (404 `INSTANCE_NOT_FOUND`) to dla okna to samo, co krok, na
  // którym nie można już decydować: ten sam komunikat „nie czeka już na Twoją decyzję", bez
  // „Spróbuj ponownie" i z odświeżeniem list.
  const instanceGone = terminalKind === 'instanceGone';
  const failed = queryFailed || terminalKind !== undefined;

  // Wiersz, na którym nie da się już decydować (także sprawa z usuniętym rekordem, z nieaktywnym
  // modułem albo niedostępna dla pytającego), jest nieaktualny — odśwież listy procesów (raz na
  // sprawę i rodzaj stanu). Z pominięciem klucza szczegółów TEJ sprawy: to on jest źródłem stanu
  // okna, a jego ponowne pobranie kasowałoby błąd i wywoływało odświeżanie w pętli.
  useEffect(() => {
    if (!notActionable && terminalKind === undefined) return;
    const ownDetailHash = hashKey(instanceDetailKey(instanceId));
    void queryClient.invalidateQueries({
      queryKey: ['processes'],
      predicate: (query) => hashKey(query.queryKey) !== ownDetailHash,
    });
  }, [notActionable, terminalKind, instanceId, queryClient]);

  if (detail !== undefined && step?.canDecide === true) {
    return (
      <AcceptanceDialog
        open
        onClose={onClose}
        initialRouteKey={initialRouteKey}
        initialStepBack={initialStepBack}
        stepInstanceId={row.stepInstanceId}
        instanceId={row.instanceId}
        routes={step.availableRoutes}
        snapshot={detail.snapshot}
        defaultRouteVoteClosesStage={step.defaultRouteVoteClosesStage}
        stepBack={step.stepBack}
        onDecided={() => onDecided()}
        testIdPrefix={testIdPrefix}
      />
    );
  }

  const finished = failed || notActionable;
  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      title={t('tenant.processes.actions.approve')}
      footer={
        <div className="flex justify-end gap-2">
          <Button
            variant="secondary"
            size="sm"
            data-testid={`${testIdPrefix}-cancel`}
            onClick={onClose}
          >
            {finished ? t('common.close') : t('common.cancel')}
          </Button>
          {failed && !recordDeleted && !moduleInactive && !instanceGone ? (
            <Button
              size="sm"
              data-testid={`${testIdPrefix}-retry`}
              loading={detailQuery.isFetching}
              onClick={() => void detailQuery.refetch()}
            >
              {t('common.retry')}
            </Button>
          ) : null}
        </div>
      }
    >
      {recordDeleted ? (
        <p
          role="alert"
          className="text-sm text-error"
          data-testid={`${testIdPrefix}-record-deleted`}
        >
          {t(TARGET_RECORD_DELETED_I18N_KEY)}
        </p>
      ) : moduleInactive ? (
        <p
          role="alert"
          className="text-sm text-error"
          data-testid={`${testIdPrefix}-module-inactive`}
        >
          {t(TARGET_MODULE_INACTIVE_I18N_KEY)}
        </p>
      ) : failed && !instanceGone ? (
        <p role="alert" className="text-sm text-error" data-testid={`${testIdPrefix}-load-error`}>
          {t('tenant.processes.approvals.decisionDialog.loadError')}
        </p>
      ) : notActionable || instanceGone ? (
        <p className="text-sm text-text-primary" data-testid={`${testIdPrefix}-not-actionable`}>
          {t('tenant.processes.approvals.decisionDialog.notActionable')}
        </p>
      ) : (
        <p className="text-sm text-text-tertiary" data-testid={`${testIdPrefix}-loading`}>
          {t('tenant.processes.approvals.loading')}
        </p>
      )}
    </Modal>
  );
}
